import React, { useState } from "react";
import { ChevronLeft, ChevronRight, X, ImageOff } from "lucide-react";
import ProtectedImage from "@/components/ProtectedImage";

/**
 * Simple swipeable/clickable photo carousel with prev/next arrows, keyboard
 * navigation, dot indicators and optional full-screen lightbox.
 */
export default function PhotoCarousel({ photos = [], altName = "Foto", testid = "carousel" }) {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  if (!photos.length) {
    return (
      <div
        className="relative w-full flex flex-col items-center justify-center text-center p-8"
        style={{
          aspectRatio: "3/4",
          background: "linear-gradient(135deg, var(--bg-elevated), var(--bg-surface))",
          border: "1px dashed var(--border-accent)",
        }}
        data-testid={`${testid}-empty`}
      >
        <ImageOff size={48} strokeWidth={1} color="var(--brand-primary)" className="mb-4 opacity-80" />
        <p className="font-serif text-2xl mb-2" style={{ color: "var(--text-primary)" }}>
          {altName}
        </p>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          Este perfil aún no tiene fotos públicas.
        </p>
      </div>
    );
  }
  const safeIdx = ((idx % photos.length) + photos.length) % photos.length;
  const next = () => setIdx((i) => (i + 1) % photos.length);
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);

  const onKey = (e) => {
    if (e.key === "ArrowRight") next();
    if (e.key === "ArrowLeft") prev();
    if (e.key === "Escape") setLightbox(false);
  };

  return (
    <div
      className="relative w-full"
      style={{ aspectRatio: "3/4", background: "var(--bg-elevated)", outline: "none" }}
      onKeyDown={onKey}
      tabIndex={0}
      data-testid={testid}
    >
      <ProtectedImage
        src={photos[safeIdx]}
        alt={`${altName} ${safeIdx + 1}`}
        className="w-full h-full cursor-zoom-in"
        onClick={() => setLightbox(true)}
        testid={`${testid}-img`}
      />

      {photos.length > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Anterior"
            className="absolute top-1/2 left-3 -translate-y-1/2 p-2 transition-opacity"
            style={{
              background: "rgba(10,10,10,0.75)",
              border: "1px solid var(--border-accent)",
              color: "var(--text-primary)",
              backdropFilter: "blur(4px)",
            }}
            data-testid={`${testid}-prev`}
          >
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <button
            onClick={next}
            aria-label="Siguiente"
            className="absolute top-1/2 right-3 -translate-y-1/2 p-2"
            style={{
              background: "rgba(10,10,10,0.75)",
              border: "1px solid var(--border-accent)",
              color: "var(--text-primary)",
              backdropFilter: "blur(4px)",
            }}
            data-testid={`${testid}-next`}
          >
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>

          <div
            className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-2"
            style={{ background: "rgba(10,10,10,0.6)", backdropFilter: "blur(6px)" }}
          >
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Ir a foto ${i + 1}`}
                style={{
                  width: i === safeIdx ? 18 : 6,
                  height: 6,
                  background: i === safeIdx ? "var(--brand-primary)" : "rgba(255,255,255,0.5)",
                  transition: "all 0.25s",
                }}
                data-testid={`${testid}-dot-${i}`}
              />
            ))}
          </div>

          <span
            className="absolute top-3 right-3 text-[10px] uppercase tracking-widest px-2 py-1"
            style={{ background: "rgba(10,10,10,0.65)", color: "var(--text-primary)" }}
          >
            {safeIdx + 1} / {photos.length}
          </span>
        </>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[9000] flex items-center justify-center p-6 cursor-zoom-out"
          style={{ background: "rgba(0,0,0,0.93)", backdropFilter: "blur(8px)" }}
          onClick={() => setLightbox(false)}
          data-testid={`${testid}-lightbox`}
        >
          <button
            className="absolute top-4 right-4 p-2"
            style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
          <ProtectedImage
            src={photos[safeIdx]}
            alt=""
            className="max-w-full max-h-full"
            style={{ background: "transparent" }}
            onClick={(e) => e.stopPropagation()}
          />
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Anterior"
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3"
                style={{ background: "rgba(0,0,0,0.5)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" }}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Siguiente"
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3"
                style={{ background: "rgba(0,0,0,0.5)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" }}
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
